// ✅ src/pages/VehicleV2/VehicleStatusReport.jsx
// FULL UPDATED FILE
//
// ✅ common localStorage / sessionStorage pattern
// ✅ useMemo + useCallback + useRef pattern
// ✅ pageLoading + dropdownLoading + listLoading
// ✅ prevents repeated initial API calls
// ✅ safer search / view all / print / export flow
// ✅ sends inventory headers
// ✅ fixes HO branch dropdown to use branch id for /list API
// ✅ sends branch name separately for print APIs
//
// APIs:
//   GET /api/vehicleStatusReport/meta
//   GET /api/vehicleStatusReport/list
//
// Print pages:
//   /vehicle/VehicleStatusReport_Print
//   /vehicle/VehicleStatusReport_SeperatePrint
//   /vehicle/VehicleStatusBranchWiseReport_Print

import React, {
  useEffect,
  useMemo,
  useState,
  useContext,
  useRef,
  useCallback,
} from "react";
import axios from "axios";
import {
  FaSearch,
  FaEye,
  FaPrint,
  FaFilter,
  FaArrowLeft,
  FaFileExport,
  FaInfoCircle,
  FaSyncAlt,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = "http://nissi-india.com/api/vehicleStatusReport";

/* ---------------- helpers ---------------- */
const toStr = (v, def = "") =>
  v === null || v === undefined ? def : String(v).trim();

const toNum = (v, d = 0) => {
  if (v === null || v === undefined || v === "") return d;
  const n = Number(String(v).replace(/,/g, ""));
  return Number.isFinite(n) ? n : d;
};

const isSelect = (v) => {
  const s = toStr(v).toLowerCase();
  return !s || s === "select";
};

const toList = (arr) => (Array.isArray(arr) ? arr : []);

const formatMoney = (v) =>
  Number(toNum(v, 0)).toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

const formatNumber = (v) =>
  Number(toNum(v, 0)).toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

const exportToCsv = (rows, filename = "vehicle-status-report.csv") => {
  if (!rows?.length) return;

  const headers = Object.keys(rows[0] || {});
  const csv = [
    headers.join(","),
    ...rows.map((r) =>
      headers
        .map((h) => {
          const val = r?.[h] ?? "";
          const s = String(val).replace(/"/g, '""');
          return `"${s}"`;
        })
        .join(",")
    ),
  ].join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const mapStatusForAspx = (vehicleStatus) => {
  const s = toStr(vehicleStatus);
  if (!s) return "Select";
  return s;
};

const createDefaultFilters = (branchType, branchId) => ({
  branch: branchType === "HO" ? "Select" : String(branchId),
  regNo: "",
  month: "Select",
  year: "Select",
  vehicleCode: "",
  vehicleName: "",
  vehicleMainType: "Select",
  vehicleStatus: "Select",
});

const buildPrintQuery = (obj) => {
  const sp = new URLSearchParams();
  Object.entries(obj).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    sp.set(k, String(v));
  });
  return sp.toString();
};

export default function VehicleStatusReport() {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);

  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  /* ===========================
     shared local/session pattern
  =========================== */
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";

  const branchIdRaw =
    localStorage.getItem("branchId") ||
    localStorage.getItem("Branchid") ||
    localStorage.getItem("branchid") ||
    "8";

  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";

  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    localStorage.getItem("loginUserId") ||
    "0";

  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "0";
  const sessionBranchId = toNum(branchIdRaw, 8);

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": String(sessionBranchId),
      "x-branch": String(branchType),
      "x-userid": String(loginUserId),
    }),
    [finYearId, finYear, sessionBranchId, branchType, loginUserId]
  );

  const session = useMemo(
    () => ({
      branchId: sessionBranchId,
      branchType: toStr(branchType || "HO"),
      loginUserId: toNum(loginUserId, 0),
      userId: toStr(userId || "0"),
    }),
    [sessionBranchId, branchType, loginUserId, userId]
  );

  const defaultFilters = useMemo(
    () => createDefaultFilters(session.branchType, session.branchId),
    [session.branchType, session.branchId]
  );

  /* ===========================
     refs
  =========================== */
  const didInitRef = useRef(false);

  /* ===========================
     loading states
  =========================== */
  const [pageLoading, setPageLoading] = useState(false);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);

  const isBusy = pageLoading || dropdownLoading || listLoading;

  /* ===========================
     UI states
  =========================== */
  const [showFilters, setShowFilters] = useState(true);

  /* ===========================
     form states
  =========================== */
  const [vehicleType, setVehicleType] = useState("own"); // own | hire
  const vehMode = vehicleType === "hire" ? "H" : "O";

  const [filters, setFilters] = useState(defaultFilters);

  /* ===========================
     meta states
  =========================== */
  const [branches, setBranches] = useState([]);
  const [mainTypes, setMainTypes] = useState([]);
  const [years, setYears] = useState([]);
  const [statuses, setStatuses] = useState(["Running", "Idle", "Maintenance"]);

  const months = useMemo(
    () => [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ],
    []
  );

  /* ===========================
     result states
  =========================== */
  const [rows, setRows] = useState([]);

  /* ===========================
     pagination
  =========================== */
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  /* ===========================
     styles
  =========================== */
  const inputClass = useMemo(
    () =>
      `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
        isDark
          ? "bg-gray-700/30 border-gray-600 text-white placeholder:text-gray-400"
          : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
      }`,
    [isDark]
  );

  const labelClass = useMemo(
    () =>
      `block text-sm font-medium mb-2 ${
        isDark ? "text-gray-300" : "text-gray-700"
      }`,
    [isDark]
  );

  const cardClass = useMemo(
    () =>
      `p-6 mt-6 rounded-2xl shadow-xl border ${
        isDark
          ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
          : "bg-white text-black"
      }`,
    [isDark]
  );

  /* ===========================
     derived helpers
  =========================== */
  const selectedBranchName = useMemo(() => {
    const selected = branches.find((b) => String(b.id) === String(filters.branch));
    return selected?.name || "";
  }, [branches, filters.branch]);

  /* ===========================
     loaders
  =========================== */
  const loadMeta = useCallback(async () => {
    try {
      setDropdownLoading(true);

      const res = await axios.get(`${API_BASE}/meta`, {
        params: {
          branchType: session.branchType,
          branchId: session.branchId,
          loginUserId: session.loginUserId,
        },
        headers: commonHeaders,
      });

      const data = res.data || {};
      if (!data?.success) {
        throw new Error(data?.message || "Meta load failed");
      }

      const branchRows = toList(data.branches);
      const typeRows = toList(data.vehicleMainTypes);

      setBranches(branchRows);
      setMainTypes(typeRows);

      if (toList(data.years).length) {
        setYears(data.years);
      } else {
        const yNow = new Date().getFullYear();
        const ys = [];
        for (let y = 2006; y <= yNow; y++) ys.push(String(y));
        setYears(ys);
      }

      if (toList(data.statuses).length) {
        setStatuses(data.statuses);
      } else {
        setStatuses(["Running", "Idle", "Maintenance"]);
      }

      if (session.branchType !== "HO") {
        setFilters((prev) => ({
          ...prev,
          branch: String(session.branchId),
        }));
      }
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || e.message || "Failed to load dropdown meta");
      setBranches([]);
      setMainTypes([]);
      setYears([]);
      setStatuses(["Running", "Idle", "Maintenance"]);
    } finally {
      setDropdownLoading(false);
    }
  }, [session.branchType, session.branchId, session.loginUserId, commonHeaders]);

  const buildListParams = useCallback(
    (f = filters, nextVehMode = vehMode) => ({
      branchType: session.branchType,
      branchId: session.branchId,
      vehicleMode: nextVehMode,

      // list API expects branch id for HO
      branch:
        session.branchType === "HO"
          ? isSelect(f.branch)
            ? "Select"
            : toStr(f.branch)
          : String(session.branchId),

      regNo: toStr(f.regNo),
      month: isSelect(f.month) ? "Select" : toStr(f.month),
      year: isSelect(f.year) ? "Select" : toStr(f.year),
      vehicleCode: toStr(f.vehicleCode),
      vehicleName: toStr(f.vehicleName),

      // backend /list uses vehicletype param, not vehicleMainType
      vehicletype: isSelect(f.vehicleMainType) ? "Select" : toStr(f.vehicleMainType),

      // backend /list uses status param, not vehicleStatus
      status: isSelect(f.vehicleStatus) ? "Select" : toStr(f.vehicleStatus),
    }),
    [filters, vehMode, session.branchType, session.branchId]
  );

  const loadList = useCallback(
    async (nextFilters = filters, nextVehMode = vehMode, silent = false) => {
      try {
        setListLoading(true);

        const res = await axios.get(`${API_BASE}/list`, {
          params: buildListParams(nextFilters, nextVehMode),
          headers: commonHeaders,
        });

        const data = res.data || {};
        if (!data?.success) {
          throw new Error(data?.message || "Failed to load report");
        }

        const list = toList(data.rows);
        setRows(list);
        setCurrentPage(1);

        if (!silent) {
          toast.success(`Found ${list.length} records`);
        }
      } catch (e) {
        console.error(e);
        toast.error(e?.response?.data?.message || e.message || "Failed to load report");
        setRows([]);
        setCurrentPage(1);
      } finally {
        setListLoading(false);
      }
    },
    [filters, vehMode, buildListParams, commonHeaders]
  );

  const loadPage = useCallback(async () => {
    try {
      setPageLoading(true);

      const baseFilters = createDefaultFilters(session.branchType, session.branchId);
      setFilters(baseFilters);
      setRows([]);
      setCurrentPage(1);

      await loadMeta();
      await loadList(baseFilters, vehMode, true);
    } finally {
      setPageLoading(false);
    }
  }, [session.branchType, session.branchId, loadMeta, loadList, vehMode]);

  /* ===========================
     handlers
  =========================== */
  const setField = useCallback((name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      setField(name, value);
    },
    [setField]
  );

  const handleVehicleTypeChange = useCallback(
    async (nextType) => {
      const nextVehMode = nextType === "hire" ? "H" : "O";
      setVehicleType(nextType);
      await loadList(filters, nextVehMode, true);
    },
    [filters, loadList]
  );

  const handleSearch = useCallback(async () => {
    await loadList(filters, vehMode);
  }, [filters, vehMode, loadList]);

  const handleViewAll = useCallback(async () => {
    const reset = createDefaultFilters(session.branchType, session.branchId);
    setFilters(reset);
    await loadList(reset, vehMode);
    toast.info("Showing all records");
  }, [session.branchType, session.branchId, vehMode, loadList]);

  const handleReset = useCallback(() => {
    const reset = createDefaultFilters(session.branchType, session.branchId);
    setFilters(reset);
    setRows([]);
    setCurrentPage(1);
    toast.info("Filters reset. Click Search to load.");
  }, [session.branchType, session.branchId]);

  const handleRefresh = useCallback(async () => {
    await loadPage();
    toast.info("Page refreshed");
  }, [loadPage]);

  const handleExport = useCallback(() => {
    if (!rows.length) {
      toast.info("No rows to export");
      return;
    }
    exportToCsv(rows, "vehicle-status-report.csv");
    toast.success("Exported successfully");
  }, [rows]);

  /* ===========================
     print
  =========================== */
  const buildAspxPrintParams = useCallback(() => {
    const f = filters;

    const monthVal = isSelect(f.month) ? "Select" : toStr(f.month);
    const yearVal = isSelect(f.year) ? "Select" : toStr(f.year);
    const mainTypeVal = isSelect(f.vehicleMainType) ? "Select" : toStr(f.vehicleMainType);
    const statusVal = mapStatusForAspx(isSelect(f.vehicleStatus) ? "" : f.vehicleStatus);

    return {
      branchType: session.branchType,
      branchId: session.branchId,
      userId: session.userId,

      vehMode,

      // print APIs in backend read branch as BRANCH NAME
      branch:
        session.branchType === "HO"
          ? selectedBranchName || "Select"
          : selectedBranchName || "Select",

      regNo: toStr(f.regNo),
      vehCode: toStr(f.vehicleCode),
      vehName: toStr(f.vehicleName),
      month: monthVal,
      year: yearVal,
      vehtype: mainTypeVal,
      vehicletype: mainTypeVal,
      vehicleMainType: mainTypeVal,
      vehicleStatus: statusVal,
      status: statusVal,
    };
  }, [filters, session.branchType, session.branchId, session.userId, vehMode, selectedBranchName]);

  const goPrintMain = useCallback(() => {
    const params = buildAspxPrintParams();
    const qs = buildPrintQuery(params);
    navigate(`/vehicle/VehicleStatusReport_Print?${qs}`, { state: params });
  }, [buildAspxPrintParams, navigate]);

  const goPrintSeparate = useCallback(() => {
    const params = buildAspxPrintParams();
    const qs = buildPrintQuery(params);
    navigate(`/vehicle/VehicleStatusReport_SeperatePrint?${qs}`, { state: params });
  }, [buildAspxPrintParams, navigate]);

  const goPrintBranchwise = useCallback(() => {
    const params = buildAspxPrintParams();
    const qs = buildPrintQuery(params);
    navigate(`/vehicle/VehicleStatusBranchWiseReport_Print?${qs}`, {
      state: params,
    });
  }, [buildAspxPrintParams, navigate]);

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  /* ===========================
     effects
  =========================== */
  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    loadPage();
  }, [loadPage]);

  /* ===========================
     pagination
  =========================== */
  const totalPages = Math.ceil(rows.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = rows.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = useCallback((p) => setCurrentPage(p), []);

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">VEHICLE STATUS REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={cardClass}>
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Vehicle Status Report
              </h1>

              <div className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Branch ID: {session.branchId || "-"} {" • "} User ID: {session.loginUserId || "-"}
              </div>

              <div className="mt-2 flex flex-wrap gap-2">
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                  }`}
                >
                  {rows.length} Records
                </span>

                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    isBusy
                      ? isDark
                        ? "bg-yellow-900 text-yellow-100"
                        : "bg-yellow-100 text-yellow-800"
                      : isDark
                      ? "bg-green-900 text-green-100"
                      : "bg-green-100 text-green-800"
                  }`}
                >
                  {pageLoading
                    ? "Loading Page"
                    : dropdownLoading
                    ? "Loading Dropdowns"
                    : listLoading
                    ? "Loading List"
                    : "Ready"}
                </span>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => setShowFilters((p) => !p)}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? `border-gray-600 text-white hover:bg-gray-800 ${
                        showFilters ? "bg-gray-800" : ""
                      }`
                    : `border-gray-300 text-gray-700 hover:bg-gray-50 ${
                        showFilters ? "bg-gray-50" : ""
                      }`
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaFilter className="w-4 h-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={handleExport}
                disabled={isBusy || rows.length === 0}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                } ${isBusy || rows.length === 0 ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaFileExport className="w-4 h-4" />
                Export
              </button>

              <button
                onClick={handleRefresh}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaSyncAlt className="w-4 h-4" />
                Refresh
              </button>

              <button
                onClick={handleBack}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaArrowLeft className="w-4 h-4" />
                Back
              </button>
            </div>
          </div>

          {(pageLoading || dropdownLoading) ? (
            <div className="text-center py-16">
              <div
                className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
                  isDark ? "bg-gray-700" : "bg-gray-100"
                }`}
              >
                <FaInfoCircle className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className={`text-xl font-semibold mb-2 ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                Loading...
              </h3>
              <p className={`mb-6 max-w-sm mx-auto ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                Please wait while data is loading.
              </p>
            </div>
          ) : (
            <>
              {/* Own/Hire */}
              <div className="flex gap-6 mb-6">
                <label className={`flex items-center gap-2 ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                  <input
                    type="radio"
                    checked={vehicleType === "own"}
                    onChange={() => handleVehicleTypeChange("own")}
                    disabled={isBusy}
                    className="text-blue-600 focus:ring-blue-500"
                  />
                  Own Vehicle
                </label>

                <label className={`flex items-center gap-2 ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                  <input
                    type="radio"
                    checked={vehicleType === "hire"}
                    onChange={() => handleVehicleTypeChange("hire")}
                    disabled={isBusy}
                    className="text-blue-600 focus:ring-blue-500"
                  />
                  Hire Vehicle
                </label>
              </div>

              {/* Filters */}
              {showFilters && (
                <div
                  className={`rounded-xl p-6 mb-6 border ${
                    isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
                  }`}
                >
                  <h3
                    className={`text-lg font-semibold mb-4 flex items-center gap-2 ${
                      isDark ? "text-white" : "text-gray-900"
                    }`}
                  >
                    <FaFilter /> FILTRATION AREA
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                    <div>
                      <label className={labelClass}>Branch</label>
                      <select
                        name="branch"
                        value={filters.branch}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy || session.branchType !== "HO"}
                      >
                        <option value="Select">Select Branch</option>
                        {branches.map((b) => (
                          <option key={b.id} value={String(b.id)}>
                            {b.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>Vehicle Reg No</label>
                      <input
                        type="text"
                        name="regNo"
                        value={filters.regNo}
                        onChange={handleChange}
                        className={inputClass}
                        placeholder="Enter registration number"
                        disabled={isBusy}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>Month</label>
                      <select
                        name="month"
                        value={filters.month}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy}
                      >
                        <option value="Select">Select Month</option>
                        {months.map((m) => (
                          <option key={m} value={m}>
                            {m}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>Year</label>
                      <select
                        name="year"
                        value={filters.year}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy}
                      >
                        <option value="Select">Select Year</option>
                        {years.map((y) => (
                          <option key={y} value={String(y)}>
                            {y}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>Vehicle Code</label>
                      <input
                        type="text"
                        name="vehicleCode"
                        value={filters.vehicleCode}
                        onChange={handleChange}
                        className={inputClass}
                        placeholder="Enter vehicle code"
                        disabled={isBusy}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>Vehicle Name</label>
                      <input
                        type="text"
                        name="vehicleName"
                        value={filters.vehicleName}
                        onChange={handleChange}
                        className={inputClass}
                        placeholder="Enter vehicle name"
                        disabled={isBusy}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>Vehicle Main Type</label>
                      <select
                        name="vehicleMainType"
                        value={filters.vehicleMainType}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy}
                      >
                        <option value="Select">Select Vehicle Main Type</option>
                        {mainTypes?.length ? (
                          mainTypes.map((t) => (
                            <option key={t.id} value={t.name}>
                              {t.name}
                            </option>
                          ))
                        ) : (
                          <>
                            <option value="TIPPER MAIN">TIPPER MAIN</option>
                            <option value="EXCAVATOR">EXCAVATOR</option>
                            <option value="DUMP TRUCK">DUMP TRUCK</option>
                            <option value="CRANE">CRANE</option>
                            <option value="LOADER">LOADER</option>
                            <option value="TRAILER">TRAILER</option>
                            <option value="BUS">BUS</option>
                          </>
                        )}
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>Vehicle Status</label>
                      <select
                        name="vehicleStatus"
                        value={filters.vehicleStatus}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy}
                      >
                        <option value="Select">Select Status</option>
                        {statuses.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                      {pageLoading
                        ? "Loading page..."
                        : dropdownLoading
                        ? "Loading dropdowns..."
                        : listLoading
                        ? "Loading list..."
                        : "Ready"}
                    </div>

                    <div className="flex flex-wrap gap-3">
                      <button
                        onClick={handleReset}
                        type="button"
                        disabled={isBusy}
                        className={`text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white font-medium transition-colors duration-200 ${
                          isBusy ? "opacity-60 cursor-not-allowed" : ""
                        }`}
                      >
                        Clear / Reset
                      </button>

                      <button
                        onClick={handleViewAll}
                        type="button"
                        disabled={isBusy}
                        className={`flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                          isBusy ? "opacity-60 cursor-not-allowed" : ""
                        }`}
                      >
                        <FaEye />
                        View All
                      </button>

                      <button
                        onClick={handleSearch}
                        type="button"
                        disabled={isBusy}
                        className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                          isBusy ? "opacity-60 cursor-not-allowed" : ""
                        }`}
                      >
                        <FaSearch />
                        Search Records
                      </button>

                      <button
                        onClick={goPrintMain}
                        type="button"
                        disabled={isBusy}
                        className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-6 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                          isBusy ? "opacity-60 cursor-not-allowed" : ""
                        }`}
                        title="Main Print"
                      >
                        <FaPrint />
                        Print Main
                      </button>

                      <button
                        onClick={goPrintSeparate}
                        type="button"
                        disabled={isBusy}
                        className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-6 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                          isBusy ? "opacity-60 cursor-not-allowed" : ""
                        }`}
                        title="Separate Print"
                      >
                        <FaPrint />
                        Print Separate
                      </button>

                      <button
                        onClick={goPrintBranchwise}
                        type="button"
                        disabled={isBusy}
                        className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-6 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                          isBusy ? "opacity-60 cursor-not-allowed" : ""
                        }`}
                        title="Branchwise Print"
                      >
                        <FaPrint />
                        Print Branchwise
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Results */}
              <div className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
                {rows.length === 0 ? (
                  <div className="text-center py-16">
                    <div
                      className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
                        isDark ? "bg-gray-700" : "bg-gray-100"
                      }`}
                    >
                      <FaInfoCircle className="w-10 h-10 text-gray-400" />
                    </div>
                    <h3 className={`text-xl font-semibold mb-2 ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                      No Vehicle Records Found
                    </h3>
                    <p className={`mb-6 max-w-sm mx-auto ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                      Try adjusting your search criteria or click "View All" to see all vehicle records.
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className={`${buttonClass} text-white`}>
                        <tr>
                          <th className="px-4 py-4 text-center font-semibold">S.No</th>
                          <th className="px-4 py-4 text-center font-semibold">Vehicle Main Type</th>
                          <th className="px-4 py-4 text-center font-semibold">Vehicle Code</th>
                          <th className="px-4 py-4 text-center font-semibold">Vehicle Name</th>
                          <th className="px-4 py-4 text-center font-semibold">Vehicle Model</th>
                          <th className="px-4 py-4 text-center font-semibold">Reg No</th>
                          <th className="px-4 py-4 text-center font-semibold">Purchase Date</th>
                          <th className="px-4 py-4 text-center font-semibold">Purchase Value</th>
                          <th className="px-4 py-4 text-center font-semibold">Cum. Hr</th>
                          <th className="px-4 py-4 text-center font-semibold">Cum. KM</th>
                          <th className="px-4 py-4 text-center font-semibold">Last Update</th>
                          <th className="px-4 py-4 text-center font-semibold">Status</th>
                        </tr>
                      </thead>

                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {currentItems.map((item, idx) => (
                          <tr
                            key={item.id ?? idx}
                            className={`${
                              isDark ? "bg-gray-800 hover:bg-gray-700" : "bg-white hover:bg-gray-50"
                            } transition-colors duration-200`}
                          >
                            <td className={`px-4 py-4 whitespace-nowrap text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                              {indexOfFirstItem + idx + 1}
                            </td>

                            <td className={`px-4 py-4 whitespace-nowrap text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                              {toStr(item.vehicleMainType)}
                            </td>

                            <td className={`px-4 py-4 whitespace-nowrap text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                              {toStr(item.vehicleCode)}
                            </td>

                            <td className={`px-4 py-4 whitespace-nowrap text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                              {toStr(item.vehicleName)}
                            </td>

                            <td className={`px-4 py-4 whitespace-nowrap text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                              {toStr(item.vehicleModel)}
                            </td>

                            <td className={`px-4 py-4 whitespace-nowrap text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                              {toStr(item.regNo)}
                            </td>

                            <td className={`px-4 py-4 whitespace-nowrap text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                              {toStr(item.purchaseDate)}
                            </td>

                            <td className={`px-4 py-4 whitespace-nowrap text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                              ₹{formatMoney(item.purchaseValue)}
                            </td>

                            <td className={`px-4 py-4 whitespace-nowrap text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                              {formatNumber(item.cumHr)}
                            </td>

                            <td className={`px-4 py-4 whitespace-nowrap text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                              {formatNumber(item.cumKm)}
                            </td>

                            <td className={`px-4 py-4 whitespace-nowrap text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                              {toStr(item.lastUpdate)}
                            </td>

                            <td
                              className={`px-4 py-4 whitespace-nowrap text-sm text-center font-semibold ${
                                String(item.status || "").toLowerCase().includes("run")
                                  ? "text-green-600"
                                  : String(item.status || "").toLowerCase().includes("idle")
                                  ? "text-yellow-600"
                                  : String(item.status || "").toLowerCase().includes("main")
                                  ? "text-red-600"
                                  : isDark
                                  ? "text-gray-200"
                                  : "text-gray-700"
                              }`}
                            >
                              {toStr(item.status)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className={`mt-4 p-4 rounded-lg ${isDark ? "bg-gray-800 text-gray-200" : "bg-blue-50 text-blue-800"}`}>
                <div className="text-center font-medium">
                  Vehicle Status Report - Total Vehicles: {rows.length}
                </div>
              </div>

              {/* Pagination */}
              {rows.length > 0 && (
                <div className="mt-6 flex flex-col sm:flex-row justify-between items-center">
                  <div className={`text-sm mb-4 sm:mb-0 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                    Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, rows.length)} of{" "}
                    {rows.length} entries
                  </div>

                  <nav className="flex gap-1">
                    <button
                      onClick={() => paginate(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className={`px-3 py-1 border rounded text-sm ${
                        currentPage === 1
                          ? isDark
                            ? "border-gray-700 text-gray-500 cursor-not-allowed"
                            : "border-gray-300 text-gray-400 cursor-not-allowed"
                          : isDark
                          ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                          : "border-gray-300 text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      Previous
                    </button>

                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) pageNum = i + 1;
                      else if (currentPage <= 3) pageNum = i + 1;
                      else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                      else pageNum = currentPage - 2 + i;

                      return (
                        <button
                          key={pageNum}
                          onClick={() => paginate(pageNum)}
                          className={`px-3 py-1 border rounded text-sm ${
                            currentPage === pageNum
                              ? `${buttonClass} text-white`
                              : isDark
                              ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                              : "border-gray-300 text-gray-700 hover:bg-gray-100"
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}

                    <button
                      onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className={`px-3 py-1 border rounded text-sm ${
                        currentPage === totalPages
                          ? isDark
                            ? "border-gray-700 text-gray-500 cursor-not-allowed"
                            : "border-gray-300 text-gray-400 cursor-not-allowed"
                          : isDark
                          ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                          : "border-gray-300 text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      Next
                    </button>
                  </nav>
                </div>
              )}

              {isBusy && (
                <div className={`mt-4 text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  {pageLoading
                    ? "Loading page..."
                    : dropdownLoading
                    ? "Loading dropdowns..."
                    : listLoading
                    ? "Loading list..."
                    : "Loading..."}
                </div>
              )}
            </>
          )}

          <ToastContainer
            position="top-right"
            autoClose={2500}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
}